import { z } from 'zod';
import { createPortalToolOutputSchema } from './portal-tool.schemas';
import { BROWSER_AUTOMATION_TOOL_NAME } from './portal-tool.constants';

export { BROWSER_AUTOMATION_TOOL_NAME };

export const BrowserAutomationActionSchema = z.enum([
  'navigate',
  'click',
  'type',
  'scroll',
  'wait',
  'get-dom-state',
  'screenshot',
  'complete',
  'list-alerts',
  'clear-alerts',
  'get-storage',
  'evaluate-js',
]);

export type BrowserAutomationAction = z.infer<
  typeof BrowserAutomationActionSchema
>;

export const BrowserAutomationHttpUrlSchema = z.url().refine(
  (value) => {
    const normalized = value.trim().toLowerCase();
    return (
      normalized.startsWith('http://') || normalized.startsWith('https://')
    );
  },
  {
    error: 'url must use http or https scheme',
  },
);

export const BrowserAutomationInputSchema = z
  .object({
    action: BrowserAutomationActionSchema,
    instanceId: z.string().min(1),
    pageId: z.string().optional(),
    url: BrowserAutomationHttpUrlSchema.optional(),
    elementIndex: z.int().positive().optional(),
    text: z.string().optional(),
    scrollDirection: z.enum(['up', 'down']).optional(),
    scrollAmount: z.int().positive().max(5000).optional(),
    waitMs: z.int().min(0).max(30000).optional(),
    completionMessage: z.string().optional(),
    script: z.string().max(8192).optional(),
  })
  .superRefine((data, ctx) => {
    switch (data.action) {
      case 'navigate':
        if (!data.url) {
          ctx.addIssue({
            code: 'custom',
            message: 'url is required for navigate action',
            path: ['url'],
          });
        }
        return;
      case 'click':
        if (data.elementIndex === undefined) {
          ctx.addIssue({
            code: 'custom',
            message: 'elementIndex is required for click action',
            path: ['elementIndex'],
          });
        }
        return;
      case 'type':
        if (data.elementIndex === undefined) {
          ctx.addIssue({
            code: 'custom',
            message: 'elementIndex is required for type action',
            path: ['elementIndex'],
          });
        }
        if (!data.text) {
          ctx.addIssue({
            code: 'custom',
            message: 'text is required for type action',
            path: ['text'],
          });
        }
        return;
      case 'evaluate-js':
        if (!data.script) {
          ctx.addIssue({
            code: 'custom',
            message: 'script is required for evaluate-js action',
            path: ['script'],
          });
        }
        return;
      default:
        return;
    }
  });

export const BrowserAutomationIpcRequestSchema =
  BrowserAutomationInputSchema.and(
    z.object({
      applicationId: z.string().min(1),
    }),
  );

export const BrowserAutomationDomElementSchema = z.object({
  highlightIndex: z.number(),
  tagName: z.string(),
  textContent: z.string(),
  cssPath: z.string(),
  xpath: z.string(),
  isInViewport: z.boolean(),
});

export const BrowserAutomationDialogEventSchema = z.object({
  type: z.enum(['alert', 'confirm', 'prompt', 'beforeunload']),
  message: z.string(),
  defaultValue: z.string().optional(),
  timestamp: z.number(),
});

export const BrowserAutomationStorageItemSchema = z.object({
  key: z.string(),
  value: z.string(),
});

export const BrowserAutomationCookieSchema = z.object({
  name: z.string(),
  value: z.string(),
  httpOnly: z.boolean().optional(),
});

export const BrowserAutomationStorageSchema = z.object({
  localStorage: z.array(BrowserAutomationStorageItemSchema),
  sessionStorage: z.array(BrowserAutomationStorageItemSchema),
  cookies: z.array(BrowserAutomationCookieSchema),
});

export const BrowserAutomationEvaluateStatusSchema = z.enum([
  'ok',
  'error',
  'timeout',
]);

export const BrowserAutomationDataSchema = z.object({
  instanceId: z.string(),
  pageId: z.string().optional(),
  url: z.string().optional(),
  title: z.string().optional(),
  domState: z.string().optional(),
  elements: z.array(BrowserAutomationDomElementSchema).optional(),
  screenshotBase64: z.string().optional(),
  completed: z.boolean().optional(),
  completionMessage: z.string().optional(),
  actionMessage: z.string().optional(),
  alerts: z.array(BrowserAutomationDialogEventSchema).optional(),
  newAlerts: z.array(BrowserAutomationDialogEventSchema).optional(),
  storage: BrowserAutomationStorageSchema.optional(),
  evaluateStatus: BrowserAutomationEvaluateStatusSchema.optional(),
  evaluateResult: z.unknown().optional(),
  evaluateError: z.string().optional(),
});

export const BrowserAutomationOutputSchema = createPortalToolOutputSchema({
  dataSchema: BrowserAutomationDataSchema,
});

export type BrowserAutomationInput = z.infer<
  typeof BrowserAutomationInputSchema
>;
export type BrowserAutomationIpcRequest = z.infer<
  typeof BrowserAutomationIpcRequestSchema
>;
export type BrowserAutomationOutput = z.infer<
  typeof BrowserAutomationOutputSchema
>;
export type BrowserAutomationData = z.infer<typeof BrowserAutomationDataSchema>;
export type BrowserAutomationDomElement = z.infer<
  typeof BrowserAutomationDomElementSchema
>;
export type BrowserAutomationDialogEvent = z.infer<
  typeof BrowserAutomationDialogEventSchema
>;
export type BrowserAutomationStorage = z.infer<
  typeof BrowserAutomationStorageSchema
>;
